import { styled } from '@linaria/react';
import { Group, Stack, TextInput } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ModuleType } from '../../api/work-periods-client/work-periods-client.type';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../components/loader/branded-loader';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { baseWhite, inkLight } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Divider } from '../../ui-library/divider/divider';
import { Icon } from '../../ui-library/icon/icon';
import { Text } from '../../ui-library/typography/typography';
import { MenuDrivenContainerNoNavbar } from '../menu-driven-container/menu-driven-container-no-navbar';
import { DropShadowContainer } from '../../components/drop-shadow-container/drop-shadow-container';
import { downloadCalculationsCSV, getMeasureMetadataId } from './calculations.helpers';
import { useCalculationsAvailableMeasures } from './calculations.hooks';
import { CalculationGroupHeader } from './components/calculation-group-header';
import { CalculationsHeader } from './components/calculations-header';
import { CalculationsTable } from './components/calculations-table';

export function Calculations() {
  const i18n = useTranslation();
  useDocumentTitle('Calculations - Bloomfilter');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  function handleClickBack() {
    navigate(-1);
  }

  const toggleSection = (type: ModuleType) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const { financialMeasures, processAnalysisMeasures, dashboardMeasures, workPeriodMeasures, isLoading } =
    useCalculationsAvailableMeasures();

  const sprintMeasures = useMemo(() => {
    return workPeriodMeasures.filter((measure) => measure.is_custom);
  }, [workPeriodMeasures]);

  const tableData = useMemo(() => {
    const allSections = [
      {
        title: i18n.t('navigation.dashboards'),
        icon: 'dashboard',
        moduleType: ModuleType.Dashboard,
        measures: Array.from(
          new Map(
            [
              ...dashboardMeasures,
              ...workPeriodMeasures.filter((measure) => measure.is_custom),
              ...processAnalysisMeasures.filter(
                (measure) => (measure.measure_name as string) !== 'workload_distribution_capacity',
              ),
            ].map((measure) => [getMeasureMetadataId(measure), measure]),
          ).values(),
        ),
      },
      {
        title: i18n.t('navigation.process_analysis'),
        icon: 'auto_graph',
        moduleType: ModuleType.ProcessAnalysis,
        measures: processAnalysisMeasures,
      },
      {
        title: i18n.t('navigation.financials'),
        icon: 'payments',
        moduleType: ModuleType.Financial,
        measures: financialMeasures,
      },
      {
        title: i18n.t('navigation.work_periods'),
        icon: 'calendar_month',
        moduleType: ModuleType.WorkPeriod,
        measures: workPeriodMeasures,
      },
    ];

    // Filter measures by search term and only return sections that have matching measures
    if (!search.trim()) {
      return allSections;
    }

    const searchTerm = search.toLowerCase().trim();
    return allSections
      .map((section) => ({
        ...section,
        measures: section.measures.filter((measure) => measure.measure_title?.toLowerCase()?.includes(searchTerm)),
      }))
      .filter((section) => section.measures.length > 0);
  }, [financialMeasures, processAnalysisMeasures, dashboardMeasures, workPeriodMeasures, search, i18n.t]);

  const handleDownloadCSV = () => {
    downloadCalculationsCSV(tableData, sprintMeasures);
  };

  return (
    <MenuDrivenContainerNoNavbar header={<CalculationsHeader onClickBack={handleClickBack} />}>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" />
      <CalculationsContainer>
        <HeaderContainer>
          <Text weight="bold" size="large">
            Calculations
          </Text>
          <Group>
            <TextInput
              placeholder="Search calculations..."
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              leftSection={<Icon name="search" size={16} color={inkLight} />}
              style={{ width: 300 }}
              styles={{
                input: {
                  borderRadius: 'var(--border-radius-xlarge)',
                  height: 36,
                },
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              radius="xl"
              leftSection={<Icon name="download" variant="filled" size={16} color={baseWhite} />}
              onClick={handleDownloadCSV}
            >
              {i18n.t('common.actions.download')}
            </Button>
          </Group>
        </HeaderContainer>
        <Divider style={{ margin: '16px 0' }} />
        <ScrollableContainer>
          <Stack>
            {tableData.map((table) => {
              const isCollapsed = collapsedSections.has(table.moduleType);
              return (
                <TableSection key={table.moduleType}>
                  <CalculationGroupHeader
                    title={table.title}
                    icon={table.icon}
                    isCollapsed={isCollapsed}
                    onToggle={() => toggleSection(table.moduleType)}
                  />
                  {!isCollapsed && (
                    <TableContainer>
                      <CalculationsTable
                        measures={table.measures}
                        showSprintColumn={table.moduleType === ModuleType.Dashboard}
                        sprintMeasures={sprintMeasures}
                      />
                    </TableContainer>
                  )}
                </TableSection>
              );
            })}
          </Stack>
        </ScrollableContainer>
      </CalculationsContainer>
    </MenuDrivenContainerNoNavbar>
  );
}

const CalculationsContainer = styled(DropShadowContainer)`
  height: calc(100vh - 150px);
  border-radius: 8px;
  width: calc(100% - 32px);
  padding: 24px 16px 16px 16px;
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ScrollableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
`;

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  margin-left: 16px;
`;
